import React, { useEffect, useMemo, useState } from 'react';
import {
  categoryTheme,
  templatePreviewFallback,
  templatePreviewImage,
} from '../utils/templatePreview';

function countTemplateSections(template) {
  const html = template?.html_code || '';
  const matches = html.match(/data-section-name="/g);
  return matches ? matches.length : 0;
}

export default function TemplateGalleryPreview({ template, className = '' }) {
  const [retry, setRetry] = useState(0);

  const category = (template?.category || 'business').toLowerCase();
  const theme = categoryTheme(category);
  const name = template?.name || 'Template';
  const sectionCount = countTemplateSections(template);

  const photoCandidates = useMemo(() => {
    const urls = [];
    for (let i = 0; i < 3; i += 1) {
      urls.push(templatePreviewImage(template, { lockOffset: i * 13 }));
    }
    urls.push(templatePreviewFallback(template));
    return [...new Set(urls)];
  }, [template?.id, template?.name, template?.category]);

  const photoSrc = photoCandidates[Math.min(retry, photoCandidates.length - 1)] || templatePreviewFallback(template);
  const showPhoto = retry < photoCandidates.length;

  useEffect(() => {
    setRetry(0);
  }, [template?.id, template?.name, template?.category]);

  return (
    <div
      className={`relative w-full aspect-[16/10] rounded-lg overflow-hidden mb-2 bg-zinc-900 group/preview ${className}`}
      style={{ boxShadow: `inset 0 0 0 1px ${theme.accent}33`, background: `linear-gradient(135deg, ${theme.accent}55, #18181b)` }}
    >
      {showPhoto ? (
        <img
          key={photoSrc}
          src={photoSrc}
          alt={`${name} preview`}
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover/preview:scale-[1.03]"
          onError={() => setRetry((n) => n + 1)}
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center px-4 text-center">
          <span className="text-white/90 text-sm font-semibold drop-shadow">{name}</span>
        </div>
      )}

      <div className="absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-black/75 via-black/35 to-transparent pointer-events-none z-[2]" />

      <div
        className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold text-white backdrop-blur-md z-[2]"
        style={{ background: `${theme.accent}cc` }}
      >
        <span>{theme.badge}</span>
        <span className="capitalize opacity-90">{category.replace('_', ' ')}</span>
      </div>

      {sectionCount > 0 && (
        <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-medium text-white/90 bg-black/50 backdrop-blur-md z-[2]">
          {sectionCount} sections
        </div>
      )}

      <div className="absolute bottom-0 inset-x-0 px-2.5 pb-2 pt-6 pointer-events-none z-[2]">
        <p className="text-white text-xs font-semibold truncate drop-shadow-sm">{name}</p>
      </div>

      <div
        className="absolute bottom-0 inset-x-0 h-0.5 z-[2]"
        style={{ background: theme.accent }}
      />
    </div>
  );
}
